import {
  type HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import type {
  NotificationVariant,
  TransactionStatus,
} from "Infrastructure/Api/Api";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import { JWT_KEY } from "Utils/AuthUtils";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";

export interface SignalRMessage {
  message: string;
  timestamp: string;
  from: string;
}

export interface SignalRNotification {
  publicID: string;
  notificationPublicID: string;
  title: string;
  content: string;
  variant: NotificationVariant;
  scheduledDate: string;
  dateCreated: string;
}

export interface SignalRTransactionStatusUpdate {
  publicID: string;
  status: TransactionStatus;
  timestamp: string;
}

class SignalRService {
  private connection: HubConnection | null = null;
  private connectionReadyCallbacks: Array<() => void> = [];

  public async startConnection(): Promise<void> {
    const apiUrl = import.meta.env.VITE_API_URL;

    if (!apiUrl) {
      logError(
        new Error("Cannot start SignalR connection without API URL"),
        { message: "VITE_API_URL is not configured" },
        false,
      );
      return;
    }

    if (
      this.connection &&
      this.connection.state !== HubConnectionState.Disconnected
    ) {
      // Avoid creating duplicate connections
      return;
    }

    const hubUrl = `${apiUrl}/hubs/notifications`;
    const token = getLocalStorageItem(JWT_KEY);

    if (!token) {
      logError(
        new Error(
          "Cannot start SignalR connection without authentication token",
        ),
        { message: "JWT token not found in localStorage" },
        false,
      );
      return;
    }

    this.connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () =>
          getLocalStorageItem(JWT_KEY)?.replace("Bearer ", "") || "",
        withCredentials: false,
      })
      .configureLogging(LogLevel.Warning)
      .withAutomaticReconnect()
      .build();

    try {
      await this.connection.start();
      // Notify all waiting callbacks that connection is ready
      this.notifyConnectionReady();
    } catch (error) {
      logError(
        error as Error,
        { message: "Error starting SignalR connection" },
        false,
      );
      throw error;
    }
  }

  private notifyConnectionReady(): void {
    const callbacks = this.connectionReadyCallbacks;
    this.connectionReadyCallbacks = [];
    for (const callback of callbacks) {
      try {
        callback();
      } catch (error) {
        logError(
          error as Error,
          { message: "Error in connection ready callback" },
          false,
        );
      }
    }
  }

  public onConnectionReady(callback: () => void): () => void {
    // If connection is already ready, call immediately
    if (
      this.connection &&
      this.connection.state === HubConnectionState.Connected
    ) {
      callback();
      return () => {}; // Return no-op cleanup
    }

    // Otherwise, queue for when connection becomes ready
    this.connectionReadyCallbacks.push(callback);

    // Return cleanup function to remove callback if component unmounts before connection ready
    return () => {
      const index = this.connectionReadyCallbacks.indexOf(callback);
      if (index !== -1) {
        this.connectionReadyCallbacks.splice(index, 1);
      }
    };
  }

  public async stopConnection(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
  }

  public onReceiveNotification(
    callback: (notification: SignalRNotification) => void,
  ): void {
    if (!this.connection) {
      logError(
        new Error(
          "Cannot subscribe to notifications: connection not established",
        ),
        { message: "Call startConnection() first" },
        false,
      );
      return;
    }
    this.connection.on("ReceiveNotification", callback);
  }

  public offReceiveNotification(
    callback: (notification: SignalRNotification) => void,
  ): void {
    // Silent no-op when connection is null - this is expected during cleanup (e.g., logout)
    if (!this.connection) {
      return;
    }
    this.connection.off("ReceiveNotification", callback);
  }

  public onReceiveTransactionStatusUpdate(
    callback: (update: SignalRTransactionStatusUpdate) => void,
  ): boolean {
    if (!this.connection) {
      logError(
        new Error(
          "Cannot subscribe to transaction status updates: connection not established",
        ),
        { message: "Call startConnection() first" },
        false,
      );
      return false;
    }
    this.connection.on("ReceiveTransactionStatusUpdate", callback);
    return true;
  }

  public offReceiveTransactionStatusUpdate(
    callback: (update: SignalRTransactionStatusUpdate) => void,
  ): void {
    // Silent no-op when connection is null - this is expected during cleanup (e.g., logout)
    if (!this.connection) {
      return;
    }
    this.connection.off("ReceiveTransactionStatusUpdate", callback);
  }

  /**
   * "Your side of the social graph changed" — pushed to the PASSIVE side of a follow, request,
   * approval, unfollow, block or visibility change (see SocialGraphChangeNotifier). Carries no
   * payload on purpose: some of those operations are silent by product rule, so the client only
   * learns that it should refetch, never what happened or who did it.
   */
  public onReceiveSocialGraphChange(callback: () => void): boolean {
    if (!this.connection) {
      logError(
        new Error(
          "Cannot subscribe to social graph changes: connection not established",
        ),
        { message: "Call startConnection() first" },
        false,
      );
      return false;
    }
    this.connection.on("ReceiveSocialGraphChange", callback);
    return true;
  }

  public offReceiveSocialGraphChange(callback: () => void): void {
    // Silent no-op when connection is null - this is expected during cleanup (e.g., logout)
    if (!this.connection) {
      return;
    }
    this.connection.off("ReceiveSocialGraphChange", callback);
  }

  public getConnectionState(): HubConnectionState {
    return this.connection?.state ?? HubConnectionState.Disconnected;
  }
}

export const signalRService = new SignalRService();
